// Modelos del agente. Centralizado para que el Director (VideoEditor), el
// Asistente (useV2Chat) y la edge function `chat-agent` usen los mismos IDs.
//
// El backend valida el `model` recibido contra una allow-list y aplica
// el pricing correcto por modelo (Opus es bastante más caro que GPT-6 Sol).

// El piso de plan de la inteligencia sale de acá, que es la fuente única del gating por
// plan (ADR-2026-09-12-la-inteligencia-del-chat-es-de-pro-para-arriba).
import { isProOrUltra } from '../../lib/planCapabilities';

// Media = GPT-6 Sol desde 2026-09-26: $2 / $0,20 cacheado / $10, rinde 40-43 en el
// Intelligence Index donde Sonnet 5 rinde 28-32, y con nuestro perfil (casi todo lectura
// de caché) sale ~35 % más barato por llamada. El backend lo sirve con un motor OpenAI
// que emite el MISMO stream SSE formato Anthropic; lo que sólo existe en Anthropic
// (investigación con Apify, anuncios de Meta) escala a Sonnet 5 en ese turno
// (ADR-2026-09-26-media-pasa-a-gpt-6-sol-y-baja-a-gemini-3-8, supersede
// ADR-2026-09-10-media-pasa-a-sonnet-5). El backend valida el id contra su tabla de
// precios: cambiarlo acá exige tenerlo ahí.
export const MODEL_MEDIA = 'gpt-6-sol';
// Alta = Opus 5.5 desde 2026-09-26: $4/$20, más barato que Opus 5 y que el 4.8 ($5/$25),
// al nivel de Fable 5.1 en la mayoría del trabajo según Anthropic. El backend lo cablea
// distinto (effort medium, avisos de progreso, fallback), ver `chatAgent.ts`
// (ADR-2026-09-26-opus-5-5-en-alta-y-en-las-animaciones).
export const MODEL_OPUS = 'claude-opus-5-5';
// Gemini Flash — nivel "baja" (ADR-608): hablar con el agente NO consume
// créditos del usuario (el costo lo absorbe Atlas). El ID es el de la
// allowlist del atlas (/v1/agent/chat), que lo mapea al modelo de Google y
// emite el MISMO stream SSE formato Anthropic que los demás. 3.8 Flash desde
// 2026-09-26, con thinking medium (a low se cae con las tools):
// ADR-2026-09-26-media-pasa-a-gpt-6-sol-y-baja-a-gemini-3-8.
export const MODEL_GEMINI_FLASH = 'gemini-3.8-flash';

// Nivel de "inteligencia" que elige el usuario en el Asistente.
//   baja  → Gemini Flash (default; gratis — no descuenta créditos).
//   media → GPT-6 Sol.
//   alta  → Opus (más potente, consume más créditos).
export type Intelligence = 'baja' | 'media' | 'alta';

export const intelligenceToModel = (lvl: Intelligence): string =>
  lvl === 'alta'
    ? MODEL_OPUS
    : lvl === 'baja'
      ? MODEL_GEMINI_FLASH
      : MODEL_MEDIA;

export const INTELLIGENCE_LABEL: Record<Intelligence, string> = {
  baja: 'Baja',
  media: 'Media',
  alta: 'Alta',
};

/**
 * El nivel que puede usar CUALQUIERA: Gemini Flash, cuyo costo absorbe Atlas (ADR-608).
 * Los otros dos consumen créditos del usuario por turno.
 */
export const INTELIGENCIA_BASE: Intelligence = 'baja';

/**
 * ¿Puede subir la inteligencia por encima de "Baja"? **Pro y Ultra** —
 * ADR-2026-09-12-la-inteligencia-del-chat-es-de-pro-para-arriba.
 *
 * En el Chat el agente está siempre (a diferencia del asistente, donde la "Ayuda de IA"
 * ya pide Pro/Ultra y sin ella el selector no aplica), así que sin este corte un Básico
 * podía poner Alta —Opus— y era el único lugar de la app donde llegaba a ese modelo.
 *
 * Plan DESCONOCIDO no bloquea, igual que `getModePlanLock`: no se le niega la opción a un
 * Pro mientras su plan carga. `entorno.plan` arranca en `''` y resuelve enseguida.
 */
export function puedeSubirInteligencia(plan?: string | null): boolean {
  if (!(plan ?? '').toString().trim()) return true;
  return isProOrUltra(plan);
}

/**
 * El nivel que se va a USAR de verdad, que no es siempre el elegido: el nivel se guarda
 * en `localStorage`, así que una cuenta que puso Alta con plan Pro y bajó a Básico
 * conserva ese `'alta'` guardado. Se recorta acá —el mismo patrón que las funciones
 * avanzadas (ADR-2026-09-12-las-funciones-avanzadas-son-de-plan-pago)—, y la preferencia
 * guardada NO se toca: si vuelve a Pro, vuelve su nivel.
 */
export function inteligenciaEfectiva(
  nivel: Intelligence,
  plan?: string | null,
): Intelligence {
  return puedeSubirInteligencia(plan) ? nivel : INTELIGENCIA_BASE;
}

// Créditos mínimos que el usuario debe tener para iniciar un turno con cada
// modelo. Es conservador: un turno con Opus puede costar varios créditos
// (sobre todo el que cambia de modelo, que paga la creación del cache a la
// tarifa de Opus). No arrancamos un request que el saldo no podría cubrir.
// Gemini Flash = 0: se puede chatear sin saldo (las generaciones que dispare
// el agente siguen cobrando lo suyo por su propio camino).
export const minCreditsForModel = (model: string): number =>
  model === MODEL_OPUS ? 6 : model === MODEL_GEMINI_FLASH ? 0 : 1;
